import { ArrowLeft, Loader2 } from 'lucide-react'
import { type ReactNode, useCallback, useRef, useState } from 'react'
import { Button } from '#app/components/ui/button.tsx'
import {
	Sheet,
	SheetContent,
	SheetHeader,
	SheetTitle,
} from '#app/components/ui/sheet.tsx'
import { getCategoryColorHex } from '#app/utils/category-colors.ts'
import { contentTypeLabels } from '#app/utils/content/content-types.ts'
import { cn } from '#app/utils/misc.tsx'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface SheetStackEntry {
	id: string
	title: string
	slug: string
	contentType: string
	contentHtml: string
	categories: Array<{ name: string; color: string }>
	relations: { id: string; title: string; slug: string; contentType: string }[]
}

type Direction = 'forward' | 'back' | null

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useSheetNavigation() {
	const [stack, setStack] = useState<SheetStackEntry[]>([])
	const [direction, setDirection] = useState<Direction>(null)
	const [isLoading, setIsLoading] = useState(false)
	const cacheRef = useRef(new Map<string, Promise<SheetStackEntry>>())
	const enrichedSlugs = useRef(new Set<string>())
	const scrollRef = useRef<HTMLDivElement | null>(null)

	const currentItem = stack.length > 0 ? stack[stack.length - 1]! : null
	const canGoBack = stack.length > 1
	const isOpen = stack.length > 0

	const resetScroll = () => {
		requestAnimationFrame(() => {
			if (scrollRef.current) scrollRef.current.scrollTop = 0
		})
	}

	const fetchPreview = useCallback((slug: string) => {
		const cached = cacheRef.current.get(slug)
		if (cached) return cached

		const promise = fetch(
			`/resources/content-sheet-preview?slug=${encodeURIComponent(slug)}`,
		)
			.then((res) => {
				if (!res.ok) throw new Error(`Preview fetch failed: ${res.status}`)
				return res.json() as Promise<SheetStackEntry>
			})
			.catch((err) => {
				// Remove from cache so a retry can re-fetch
				cacheRef.current.delete(slug)
				throw err
			})

		cacheRef.current.set(slug, promise)
		return promise
	}, [])

	const open = useCallback((item: SheetStackEntry) => {
		cacheRef.current.clear()
		enrichedSlugs.current.clear()
		setDirection(null)
		setIsLoading(false)
		setStack([item])
	}, [])

	const close = useCallback(() => {
		setStack([])
		setDirection(null)
		setIsLoading(false)
		enrichedSlugs.current.clear()
	}, [])

	const navigateTo = useCallback(
		async (slug: string) => {
			setIsLoading(true)
			try {
				const entry = await fetchPreview(slug)
				setStack((prev) => [...prev, entry])
				setDirection('forward')
				resetScroll()
			} catch (err) {
				console.error('Sheet navigation failed:', err)
			} finally {
				setIsLoading(false)
			}
		},
		[fetchPreview],
	)

	const goBack = useCallback(() => {
		setStack((prev) => (prev.length > 1 ? prev.slice(0, -1) : prev))
		setDirection('back')
		resetScroll()
	}, [])

	const preload = useCallback(
		(slug: string) => {
			void fetchPreview(slug)
		},
		[fetchPreview],
	)

	const enrichCurrent = useCallback(
		async (slug: string) => {
			if (enrichedSlugs.current.has(slug)) return
			try {
				const entry = await fetchPreview(slug)
				enrichedSlugs.current.add(slug)
				setStack((prev) => {
					if (prev.length === 0) return prev
					const last = prev[prev.length - 1]!
					if (last.slug !== slug) return prev
					return [...prev.slice(0, -1), { ...last, relations: entry.relations }]
				})
			} catch {
				// Silently ignore — relations are non-critical
			}
		},
		[fetchPreview],
	)

	const clearDirection = useCallback(() => {
		setDirection(null)
	}, [])

	return {
		currentItem,
		canGoBack,
		isOpen,
		isLoading,
		direction,
		scrollRef,
		open,
		close,
		navigateTo,
		goBack,
		preload,
		enrichCurrent,
		clearDirection,
	}
}

// ---------------------------------------------------------------------------
// Shared body component
// ---------------------------------------------------------------------------

interface SheetContentBodyProps {
	item: SheetStackEntry
	isLoading: boolean
	direction: Direction
	onNavigate: (slug: string) => void
	onPreload: (slug: string) => void
	onAnimationEnd: () => void
}

export function SheetContentBody({
	item,
	isLoading,
	direction,
	onNavigate,
	onPreload,
	onAnimationEnd,
}: SheetContentBodyProps) {
	if (isLoading) {
		return (
			<div className="flex items-center justify-center py-16">
				<Loader2 className="text-muted-foreground h-6 w-6 animate-spin" />
			</div>
		)
	}

	const animationClass = direction
		? cn(
				'animate-in duration-200',
				direction === 'forward'
					? 'slide-in-from-right-4'
					: 'slide-in-from-left-4',
			)
		: undefined

	return (
		<div
			key={item.slug}
			className={animationClass}
			onAnimationEnd={onAnimationEnd}
		>
			<article
				className="content-view"
				dangerouslySetInnerHTML={{ __html: item.contentHtml }}
			/>
			{item.relations.length > 0 && (
				<section className="border-border mt-6 border-t pt-4">
					<h3 className="text-muted-foreground mb-2 text-xs font-semibold tracking-[0.15em] uppercase">
						Contenu lié
					</h3>
					<ul className="divide-border/60 divide-y">
						{item.relations.map((rel) => (
							<li key={rel.id}>
								<button
									type="button"
									onClick={() => onNavigate(rel.slug)}
									onTouchStart={() => onPreload(rel.slug)}
									className="hover:bg-muted/60 flex w-full flex-col gap-1 py-3 text-left transition-colors"
								>
									<span className="text-sm font-medium">{rel.title}</span>
									<span className="bg-muted text-muted-foreground w-fit rounded-full px-2 py-0.5 text-[11px] font-medium">
										{contentTypeLabels[rel.contentType] ?? rel.contentType}
									</span>
								</button>
							</li>
						))}
					</ul>
				</section>
			)}
		</div>
	)
}

// ---------------------------------------------------------------------------
// Back button for sheet header
// ---------------------------------------------------------------------------

export function SheetBackButton({ onBack }: { onBack: () => void }) {
	return (
		<Button
			type="button"
			variant="ghost"
			size="icon"
			className="h-touch w-touch -ml-2 shrink-0"
			onClick={onBack}
			aria-label="Retour"
		>
			<ArrowLeft className="h-4 w-4" />
		</Button>
	)
}

// ---------------------------------------------------------------------------
// Preview sheet — reusable wrapper around Sheet + header + SheetContentBody
// ---------------------------------------------------------------------------

type SheetNavigation = ReturnType<typeof useSheetNavigation>

interface ContentPreviewSheetProps {
	sheetNav: SheetNavigation
	/** Replaces the default category pill in the header's trailing row. */
	headerTrailing?: ReactNode
}

export function ContentPreviewSheet({
	sheetNav,
	headerTrailing,
}: ContentPreviewSheetProps) {
	const item = sheetNav.currentItem
	const trailing =
		headerTrailing ??
		(item?.categories.length
			? item.categories.map((cat) => {
					const hex = getCategoryColorHex(cat.color)
					return (
						<span
							key={cat.name}
							className="rounded-full px-2.5 py-0.5 text-[11px] font-semibold tracking-wide uppercase"
							style={{
								backgroundColor: `${hex}20`,
								color: hex,
							}}
						>
							{cat.name}
						</span>
					)
				})
			: null)

	return (
		<Sheet
			open={sheetNav.isOpen}
			onOpenChange={(open) => {
				if (!open) sheetNav.close()
			}}
		>
			<SheetContent
				side="bottom"
				className="gap-0 rounded-t-none border-0 p-0"
				onOpenAutoFocus={(e) => e.preventDefault()}
			>
				<SheetHeader className="border-border border-b p-4 pr-12">
					<div className="flex items-center gap-2">
						{sheetNav.canGoBack && <SheetBackButton onBack={sheetNav.goBack} />}
						<SheetTitle className="min-w-0 truncate">
							{item?.title ?? 'Aperçu'}
						</SheetTitle>
					</div>
					<div className="flex flex-wrap items-center gap-2">
						{sheetNav.canGoBack && item && (
							<span className="bg-muted text-muted-foreground rounded-full px-2 py-0.5 text-[11px] font-medium">
								{contentTypeLabels[item.contentType] ?? item.contentType}
							</span>
						)}
						{trailing}
					</div>
				</SheetHeader>
				<div
					ref={sheetNav.scrollRef}
					className="max-h-[70dvh] flex-1 overflow-y-auto p-4"
				>
					{item && (
						<SheetContentBody
							item={item}
							isLoading={sheetNav.isLoading}
							direction={sheetNav.direction}
							onNavigate={sheetNav.navigateTo}
							onPreload={sheetNav.preload}
							onAnimationEnd={sheetNav.clearDirection}
						/>
					)}
				</div>
			</SheetContent>
		</Sheet>
	)
}
